import {
  ClockCircleOutlined,
  EditOutlined,
  FileTextOutlined,
  UserOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Card,
  Col,
  Divider,
  Dropdown,
  Form,
  Input,
  List,
  MenuProps,
  Modal,
  Row,
  Tag,
  Typography,
} from "antd";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { formatDataInYYYYMMDDFormatWithTime } from "../../helpers/date";
import { priorityColors, statusColors } from "../../helpers/tickets";
import useAxiosPost from "../../hooks/useAxiosPost";
import useMultiNotification from "../../hooks/useNotification";
import NetwalaButton from "../Items/Button/Index";
import TailSpinLoader from "../Items/Loaders/TailSpinLoader";
import "./support-tickets.css";
import { ButtonProps } from "../../helpers/constants";
import AssignTicketToStaffModal from "../Modals/AssignTicketToStaffModal";
const { Title, Text } = Typography;

const ViewSupportTicketDetailsComponent = () => {
  const { id } = useParams();
  const { openNotification, contextHolder } = useMultiNotification();
  const [ticketDetails, setTicketDetails] = useState<ITicket | null>(null);
  const [showAssignTicketToStaffModal, setShowAssignTicketToStaffModal] =
    useState(false);
  const [form] = Form.useForm();
  const [messages, setMessages] = useState<ITicketMessage[]>([]);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [statusDropdownVisible, setStatusDropdownVisible] = useState(false);
  const [priorityDropdownVisible, setPriorityDropdownVisible] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [selectedPriority, setSelectedPriority] = useState<string>("");
  const [showStatusConfirmModal, setShowStatusConfirmModal] = useState(false);
  const [showPriorityConfirmModal, setShowPriorityConfirmModal] =
    useState(false);
  const [
    postApiData,
    apiResponse,
    apiError,
    apiLoading,
    apiMessage,
    apiErrorCount,
  ] = useAxiosPost<ResponseProp>("support/tickets/getTicketById");
  const [
    postAddNewMessageToTicketApiData,
    addNewMessageToTicketApiResponse,
    addNewMessageToTicketApiError,
    addNewMessageToTicketApiLoading,
    addNewMessageToTicketApiMessage,
    addNewMessageToTicketApiErrorCount,
  ] = useAxiosPost<ResponseProp>("support/tickets/addNewMessageToTicket");
  const [
    postAssignTicketToStaffApiData,
    assignTicketToStaffApiResponse,
    assignTicketToStaffApiError,
    assignTicketToStaffApiLoading,
    assignTicketToStaffApiMessage,
    assignTicketToStaffApiErrorCount,
  ] = useAxiosPost<ResponseProp>("support/tickets/assignTicketToStaff");
  const [
    postUpdateTicketStatusApiData,
    updateTicketStatusApiResponse,
    updateTicketStatusApiError,
    updateTicketStatusApiLoading,
    updateTicketStatusApiMessage,
    updateTicketStatusApiErrorCount,
    setUpdateTicketStatusUrl,
  ] = useAxiosPost<ResponseProp>("support/tickets/updateTicketStatus");
  const [
    postUpdateTicketPriorityApiData,
    updateTicketPriorityApiResponse,
    updateTicketPriorityApiError,
    updateTicketPriorityApiLoading,
    updateTicketPriorityApiMessage,
    updateTicketPriorityApiErrorCount,
  ] = useAxiosPost<ResponseProp>("support/tickets/updatePriorityOfTicket");
  useEffect(() => {
    if (apiResponse !== null) {
      const data: any = apiResponse.responseData;
      setTicketDetails(data);
      setMessages(data.messages);
      // Scroll to bottom after ticket details load
      setTimeout(() => {
        scrollToBottom();
      }, 300);
    }
  }, [apiResponse]);
  useEffect(() => {
    postApiData({ id });
  }, []);

  useEffect(() => {
    if (addNewMessageToTicketApiResponse !== null) {
      const data: any = addNewMessageToTicketApiResponse.responseData;
      setTicketDetails(data);
      setMessages(data.messages);
    }
  }, [addNewMessageToTicketApiResponse]);
  useEffect(() => {
    if (
      addNewMessageToTicketApiError !== null &&
      addNewMessageToTicketApiErrorCount !== 0
    ) {
      openNotification("error", "Error", addNewMessageToTicketApiError);
    }
  }, [addNewMessageToTicketApiError, addNewMessageToTicketApiErrorCount]);
  useEffect(() => {
    if (assignTicketToStaffApiResponse !== null) {
      const data: any = assignTicketToStaffApiResponse.responseData;
      setTicketDetails(data);
      setMessages(data.messages);
      if (assignTicketToStaffApiResponse.message) {
        openNotification(
          "success",
          "Success",
          assignTicketToStaffApiResponse.message
        );
      }
    }
  }, [assignTicketToStaffApiResponse]);
  useEffect(() => {
    if (
      assignTicketToStaffApiError !== null &&
      assignTicketToStaffApiErrorCount !== 0
    ) {
      openNotification("error", "Error", assignTicketToStaffApiError);
    }
  }, [assignTicketToStaffApiError, assignTicketToStaffApiErrorCount]);
  useEffect(() => {
    if (updateTicketStatusApiResponse !== null) {
      const data: any = updateTicketStatusApiResponse.responseData;
      setTicketDetails(data);
      setMessages(data.messages);
      if (updateTicketStatusApiResponse.message) {
        openNotification(
          "success",
          "Success",
          updateTicketStatusApiResponse.message
        );
      }
    }
  }, [updateTicketStatusApiResponse]);
  useEffect(() => {
    if (
      updateTicketStatusApiError !== null &&
      updateTicketStatusApiErrorCount !== 0
    ) {
      openNotification("error", "Error", updateTicketStatusApiError);
    }
  }, [updateTicketStatusApiError, updateTicketStatusApiErrorCount]);
  useEffect(() => {
    if (updateTicketPriorityApiResponse !== null) {
      const data: any = updateTicketPriorityApiResponse.responseData;
      setTicketDetails(data);
      setMessages(data.messages);
      if (updateTicketPriorityApiResponse.message) {
        openNotification(
          "success",
          "Success",
          updateTicketPriorityApiResponse.message
        );
      }
    }
  }, [updateTicketPriorityApiResponse]);
  useEffect(() => {
    if (
      updateTicketPriorityApiError !== null &&
      updateTicketPriorityApiErrorCount !== 0
    ) {
      openNotification("error", "Error", updateTicketPriorityApiError);
    }
  }, [updateTicketPriorityApiError, updateTicketPriorityApiErrorCount]);
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      // Use requestAnimationFrame for smooth scrolling
      requestAnimationFrame(() => {
        if (container.scrollHeight > container.clientHeight) {
          container.scrollTop = container.scrollHeight;
        }
      });
    }
  };

  useEffect(() => {
    // Scroll on initial load
    scrollToBottom();
  }, []);

  useEffect(() => {
    // Scroll when messages change - use multiple attempts to ensure it works
    const timer1 = setTimeout(() => {
      scrollToBottom();
    }, 50);

    const timer2 = setTimeout(() => {
      scrollToBottom();
    }, 200);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [messages]);

  const handleSend = (values: any) => {
    const trimmedMessage = values.message?.trim();
    if (!trimmedMessage) {
      return;
    }
    postAddNewMessageToTicketApiData({
      id: id,
      message: trimmedMessage,
    });
    form.resetFields();
  };

  const handleCloseAssignTicketToStaffModal = (
    isAssignmentDone: boolean,
    staffId: number | null
  ) => {
    if (isAssignmentDone && staffId) {
      postAssignTicketToStaffApiData({ id, intPkHeadID: staffId });
    }
    setShowAssignTicketToStaffModal(false);
  };

  const handleStatusChange = (value: string) => {
    if (value === "CLOSED") {
      setUpdateTicketStatusUrl("support/tickets/updateTicketToClosed");
    } else if (value === "RESOLVED") {
      setUpdateTicketStatusUrl("support/tickets/updateTicketToResolved");
    } else if (value === "REOPENED") {
      setUpdateTicketStatusUrl("support/tickets/updateTicketToReopened");
    }
    setSelectedStatus(value);
    setShowStatusConfirmModal(true);
  };

  const handlePriorityChange = (value: string) => {
    setSelectedPriority(value);
    setShowPriorityConfirmModal(true);
  };

  const allStatusOptions = [{ key: "CLOSED", label: "Close" }];

  const allPriorityOptions = [
    { key: "LOW", label: "LOW" },
    { key: "MEDIUM", label: "MEDIUM" },
    { key: "HIGH", label: "HIGH" },
    { key: "CRITICAL", label: "CRITICAL" },
  ];

  const statusMenuItems: MenuProps["items"] = allStatusOptions
    .filter((option) => option.key !== ticketDetails?.status)
    .map((option) => ({
      key: option.key,
      label: (
        <Tag
          color={statusColors[option.key as keyof typeof statusColors]}
          style={{ margin: 0 }}
        >
          {option.label}
        </Tag>
      ),
      onClick: () => handleStatusChange(option.key),
    }));

  const priorityMenuItems: MenuProps["items"] = allPriorityOptions
    .filter((option) => option.key !== ticketDetails?.priority)
    .map((option) => ({
      key: option.key,
      label: (
        <Tag
          color={priorityColors[option.key as keyof typeof priorityColors]}
          style={{ margin: 0 }}
        >
          {option.label}
        </Tag>
      ),
      onClick: () => handlePriorityChange(option.key),
    }));

  const handleConfirmStatusUpdate = () => {
    updateTicketStatus(selectedStatus);
    setShowStatusConfirmModal(false);
    setSelectedStatus("");
  };

  const handleConfirmPriorityUpdate = () => {
    updateTicketPriority(selectedPriority);
    setShowPriorityConfirmModal(false);
    setSelectedPriority("");
  };
  const callStatusUpdateApi = (status: string) => {
    postUpdateTicketStatusApiData({ id, status: status });
  };
  const updateTicketStatus = (newStatus: string) => {
    callStatusUpdateApi(newStatus);
    // TODO: Call API to update status
  };

  const updateTicketPriority = (newPriority: string) => {
    postUpdateTicketPriorityApiData({ id, priority: newPriority });
    // TODO: Call API to update priority
  };
  if (
    apiLoading ||
    !ticketDetails ||
    apiError ||
    assignTicketToStaffApiLoading ||
    updateTicketStatusApiLoading ||
    updateTicketPriorityApiLoading
  ) {
    return <TailSpinLoader />;
  }

  return (
    <>
      {contextHolder}
      {showAssignTicketToStaffModal && (
        <AssignTicketToStaffModal
          show={showAssignTicketToStaffModal}
          handleClose={handleCloseAssignTicketToStaffModal}
        />
      )}
      <Modal
        title="Confirm Status Update"
        open={showStatusConfirmModal}
        onOk={handleConfirmStatusUpdate}
        onCancel={() => {
          setShowStatusConfirmModal(false);
          setSelectedStatus("");
        }}
        okText="Confirm"
        cancelText="Cancel"
      >
        <p>
          Are you sure you want to update the status to{" "}
          <strong>{selectedStatus}</strong>?
        </p>
      </Modal>
      <Modal
        title="Confirm Priority Update"
        open={showPriorityConfirmModal}
        onOk={handleConfirmPriorityUpdate}
        onCancel={() => {
          setShowPriorityConfirmModal(false);
          setSelectedPriority("");
        }}
        okText="Confirm"
        cancelText="Cancel"
      >
        <p>
          Are you sure you want to update the priority to{" "}
          <strong>{selectedPriority}</strong>?
        </p>
      </Modal>
      <div style={{ maxWidth: 800, margin: "0 auto" }}>
        {/* Ticket Header */}
        <Card
          style={{
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            borderRadius: 8,
            border: "1px solid #f0f0f0",
          }}
          bodyStyle={{ padding: 24 }}
        >
          {/* Header Section */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 24,
              paddingBottom: 16,
              borderBottom: "2px solid #f0f0f0",
            }}
          >
            <div>
              <Title
                level={3}
                style={{ margin: 0, fontSize: 20, fontWeight: 600 }}
              >
                Complaint #{ticketDetails.ticketId}
              </Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Complaint Details
              </Text>
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                {ticketDetails.status === "CLOSED" ? (
                  <Tag
                    color={
                      statusColors[
                        ticketDetails.status as keyof typeof statusColors
                      ]
                    }
                    style={{
                      padding: "4px 12px",
                      fontSize: 13,
                      fontWeight: 500,
                      borderRadius: 4,
                      border: "none",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {ticketDetails.status}
                  </Tag>
                ) : (
                  <Dropdown
                    menu={{ items: statusMenuItems }}
                    trigger={["click"]}
                    onOpenChange={(open) => setStatusDropdownVisible(open)}
                  >
                    <Tag
                      color={
                        statusColors[
                          ticketDetails.status as keyof typeof statusColors
                        ]
                      }
                      style={{
                        padding: "4px 12px",
                        fontSize: 13,
                        fontWeight: 500,
                        borderRadius: 4,
                        border: "none",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      {ticketDetails.status}
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(0, 0, 0, 0.4)",
                          borderRadius: "50%",
                          width: "18px",
                          height: "18px",
                          marginLeft: "4px",
                          border: "1px solid rgba(255, 255, 255, 0.6)",
                          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
                        }}
                      >
                        <EditOutlined
                          style={{
                            color: "#fff",
                            fontSize: 11,
                            fontWeight: "bold",
                          }}
                        />
                      </span>
                    </Tag>
                  </Dropdown>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                {ticketDetails.status === "CLOSED" ? (
                  <Tag
                    color={
                      priorityColors[
                        ticketDetails.priority as keyof typeof priorityColors
                      ]
                    }
                    style={{
                      padding: "4px 12px",
                      fontSize: 13,
                      fontWeight: 500,
                      borderRadius: 4,
                      border: "none",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {ticketDetails.priority}
                  </Tag>
                ) : (
                  <Dropdown
                    menu={{ items: priorityMenuItems }}
                    trigger={["click"]}
                    onOpenChange={(open) => setPriorityDropdownVisible(open)}
                  >
                    <Tag
                      color={
                        priorityColors[
                          ticketDetails.priority as keyof typeof priorityColors
                        ]
                      }
                      style={{
                        padding: "4px 12px",
                        fontSize: 13,
                        fontWeight: 500,
                        borderRadius: 4,
                        border: "none",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      {ticketDetails.priority}
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(0, 0, 0, 0.4)",
                          borderRadius: "50%",
                          width: "18px",
                          height: "18px",
                          marginLeft: "4px",
                          border: "1px solid rgba(255, 255, 255, 0.6)",
                          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
                        }}
                      >
                        <EditOutlined
                          style={{
                            color: "#fff",
                            fontSize: 11,
                            fontWeight: "bold",
                          }}
                        />
                      </span>
                    </Tag>
                  </Dropdown>
                )}
              </div>
            </div>
          </div>

          {/* Content Section */}
          <Row gutter={[24, 16]}>
            <Col span={24}>
              <div style={{ marginBottom: 20 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 8,
                    gap: 8,
                  }}
                >
                  <FileTextOutlined
                    style={{ color: "#1890ff", fontSize: 16 }}
                  />
                  <Text strong style={{ fontSize: 14, color: "#595959" }}>
                    Subject
                  </Text>
                </div>
                <Text
                  style={{
                    fontSize: 15,
                    color: "#262626",
                    display: "block",
                    marginLeft: 24,
                  }}
                >
                  {ticketDetails.title}
                </Text>
              </div>
            </Col>

            <Col span={24}>
              <div style={{ marginBottom: 20 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 8,
                    gap: 8,
                  }}
                >
                  <FileTextOutlined
                    style={{ color: "#1890ff", fontSize: 16 }}
                  />
                  <Text strong style={{ fontSize: 14, color: "#595959" }}>
                    Description
                  </Text>
                </div>
                <Text
                  style={{
                    fontSize: 14,
                    color: "#595959",
                    display: "block",
                    marginLeft: 24,
                    lineHeight: 1.6,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {ticketDetails.description}
                </Text>
              </div>
            </Col>
          </Row>

          <Divider style={{ margin: "20px 0" }} />

          {/* Metadata Section */}
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={12}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "12px 0",
                }}
              >
                <ClockCircleOutlined
                  style={{ color: "#8c8c8c", fontSize: 18 }}
                />
                <div>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, display: "block", lineHeight: 1.4 }}
                  >
                    Created At
                  </Text>
                  <Text
                    style={{ fontSize: 14, fontWeight: 500, display: "block" }}
                  >
                    {formatDataInYYYYMMDDFormatWithTime(
                      new Date(ticketDetails.createdAt)
                    )}
                  </Text>
                </div>
              </div>
            </Col>

            <Col xs={24} sm={12}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "12px 0",
                }}
              >
                <UserOutlined style={{ color: "#8c8c8c", fontSize: 18 }} />
                <div>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, display: "block", lineHeight: 1.4 }}
                  >
                    Created By
                  </Text>
                  <Text
                    style={{ fontSize: 14, fontWeight: 500, display: "block" }}
                  >
                    {ticketDetails.createdByName}
                  </Text>
                </div>
              </div>
            </Col>

            <Col xs={24} sm={12}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "12px 0",
                }}
              >
                <UserSwitchOutlined
                  style={{ color: "#8c8c8c", fontSize: 18 }}
                />
                <div>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, display: "block", lineHeight: 1.4 }}
                  >
                    Assigned To
                  </Text>
                  <Text
                    style={{ fontSize: 14, fontWeight: 500, display: "block" }}
                  >
                    {ticketDetails.assignedToName || (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <Text type="secondary" style={{ fontStyle: "italic" }}>
                          Unassigned
                        </Text>
                        <NetwalaButton
                          backgroundColor={ButtonProps.background.STANDARD}
                          height={ButtonProps.height.STANDARD}
                          textColor={ButtonProps.color.STANDARD}
                          width={ButtonProps.width.ONE24}
                          text="Assign to Staff"
                          onClick={() => {
                            setShowAssignTicketToStaffModal(true);
                          }}
                        />
                      </div>
                    )}
                  </Text>
                </div>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "12px 0",
                }}
              >
                <UserSwitchOutlined
                  style={{ color: "#8c8c8c", fontSize: 18 }}
                />
                <div>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, display: "block", lineHeight: 1.4 }}
                  >
                    Connection Details
                  </Text>
                  <Text
                    style={{ fontSize: 14, fontWeight: 500, display: "block" }}
                  >
                    {ticketDetails.connectionFullName} -{" "}
                    {ticketDetails.connectionPhoneNumber}
                  </Text>
                </div>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Messages and Input Side by Side */}
        <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
          <Col xs={24} sm={24} md={16} lg={16}>
            <Card
              title="Comments"
              className="messages-card-container"
              style={{ height: "100%" }}
              bodyStyle={{
                flex: 1,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                padding: 16,
              }}
            >
              <div
                ref={messagesContainerRef}
                className="messages-scroll-container"
              >
                <List
                  dataSource={messages}
                  style={{ padding: 0 }}
                  renderItem={(msg) => (
                    <List.Item style={{ border: "none", padding: "12px 0" }}>
                      <List.Item.Meta
                        avatar={<Avatar>{msg.senderName[0]}</Avatar>}
                        title={
                          <div style={{ fontWeight: 600 }}>
                            {msg.senderName}{" "}
                            <span style={{ fontSize: 12, color: "#888" }}>
                              {formatDataInYYYYMMDDFormatWithTime(
                                new Date(msg.createdAt)
                              )}
                            </span>
                          </div>
                        }
                        description={
                          <div
                            style={{
                              background: "#f5f5f5",
                              padding: "10px 14px",
                              borderRadius: 10,
                              display: "inline-block",
                              marginTop: 4,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                flexWrap: "wrap",
                              }}
                            >
                              <span>{msg.message}</span>
                              {msg.isTicketAssigned && (
                                <Tag
                                  color="blue"
                                  style={{
                                    margin: 0,
                                    fontSize: 11,
                                    padding: "2px 8px",
                                    borderRadius: 4,
                                  }}
                                >
                                  Ticket Assigned
                                </Tag>
                              )}
                              {msg.isTicketClosed && (
                                <Tag
                                  color="red"
                                  style={{
                                    margin: 0,
                                    fontSize: 11,
                                    padding: "2px 8px",
                                    borderRadius: 4,
                                  }}
                                >
                                  Ticket Closed
                                </Tag>
                              )}
                              {msg.isTicketResolved && (
                                <Tag
                                  color="green"
                                  style={{
                                    margin: 0,
                                    fontSize: 11,
                                    padding: "2px 8px",
                                    borderRadius: 4,
                                  }}
                                >
                                  Ticket Resolved
                                </Tag>
                              )}
                              {msg.isTicketReopened && (
                                <Tag
                                  color="orange"
                                  style={{
                                    margin: 0,
                                    fontSize: 11,
                                    padding: "2px 8px",
                                    borderRadius: 4,
                                  }}
                                >
                                  Ticket Reopened
                                </Tag>
                              )}
                              {msg.isTicketPriorityUpdated && (
                                <Tag
                                  color="purple"
                                  style={{
                                    margin: 0,
                                    fontSize: 11,
                                    padding: "2px 8px",
                                    borderRadius: 4,
                                  }}
                                >
                                  Priority Updated
                                </Tag>
                              )}
                            </div>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={24} md={8} lg={8}>
            <Card
              title="Comment"
              style={{
                minHeight: 500,
              }}
              bodyStyle={{ padding: 16 }}
            >
              <Form form={form} onFinish={handleSend}>
                <Form.Item
                  name="message"
                  rules={[
                    { required: true, message: "Enter a message" },
                    {
                      validator: (_, value) => {
                        if (!value || !value.trim()) {
                          return Promise.reject(
                            new Error(
                              "Comment cannot be empty or contain only spaces"
                            )
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  style={{ marginBottom: 16 }}
                >
                  <Input.TextArea
                    rows={10}
                    placeholder="Write a comment..."
                    style={{ resize: "none" }}
                  />
                </Form.Item>
                <NetwalaButton
                  htmlType="submit"
                  onClick={() => {}}
                  text={
                    addNewMessageToTicketApiLoading ? "Sending..." : "Comment"
                  }
                  disabled={addNewMessageToTicketApiLoading}
                />
              </Form>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default ViewSupportTicketDetailsComponent;
